import React, { FC, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import TextCustomization from '../../customizations/TextCustomization';
import { useAppSelector } from '../../../app/hooks';
import { selectPersona } from '../../../app/slices/persona.slice';
import { selectBankAccount } from '../../../app/slices/bank.slice';
import { ReactComponent as LogoutIcon } from '../../../assets/images/icons/log-out.svg';
import { ReactComponent as UserIcon } from '../../../assets/images/icons/user.svg';
import { ReactComponent as FileTextIcon } from '../../../assets/images/icons/file-text.svg';
import DateConverter, { DateFormat } from '../../../utils/DateConverter';
import { Country } from '../../../constants/country-phone-number';
import { useAppTranslation } from '../../../i18n';

export const Header: FC = () => {
	const theme = useTheme();
	const { t } = useAppTranslation();
	const persona = useAppSelector(selectPersona);
	const bankAccount = useAppSelector(selectBankAccount);
	const [isOpenBankAccount, setIsOpenBankAccount] = useState(false);

	const showBankAccountDetails = bankAccount.accountHolder;
	const toggleBankAccountDetails = () => setIsOpenBankAccount((prev) => !prev);

	const getColoredIcon = (Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>, opacity?: string) => (
		<Icon className="user-icon" fill={theme.palette.primary.main} style={opacity ? { opacity } : undefined} />
	);

	const hasPersonaDetails = persona?.dateOfBirth && persona?.country;
	const countryName = persona?.country ? capitalizeFirstLetter(t(`countries.${persona.country as Country}`)) : '';

	return (
		<Box className="content-header">
			<InfoSection icon={getColoredIcon(LogoutIcon)}>
				<div className="single-content-info">
					<div className="user-info-text" role="heading" aria-level={2} style={{ padding: 0 }}>
						<TextCustomization id="RESULTS_PAGE_TITLE" />
					</div>
				</div>
			</InfoSection>
			<InfoSection icon={getColoredIcon(UserIcon)}>
				<div className="user-info-container">
					<div className="user-info-text">
						{persona.firstName} {persona.lastName}
					</div>
					{hasPersonaDetails && (
						<div className="user-info-text-complete">
							{DateConverter.convert(persona.dateOfBirth, DateFormat.VIEW_FORMAT)}, {countryName}
						</div>
					)}
				</div>
			</InfoSection>
			{showBankAccountDetails && (
				<>
					<InfoSection icon={getColoredIcon(FileTextIcon)}>
						<div className="single-content-info">
							<div className="user-info-text" style={{ padding: 0 }}>
								{t(`results-page.header.bank account details`)}
							</div>
							<div className="user-info-details">
								<button onClick={toggleBankAccountDetails}>Details</button>
							</div>
						</div>
					</InfoSection>
					{isOpenBankAccount && (
						<BankAccountInformation icon={getColoredIcon(FileTextIcon)} {...bankAccount} />
					)}
				</>
			)}
		</Box>
	);
};

interface InfoSectionProps {
	icon: React.ReactElement;
	children: React.ReactNode;
	className?: string;
}

const InfoSection: FC<InfoSectionProps> = ({ icon, children, className = '' }) => (
	<div className={`container-personal-info ${className}`}>
		<div className="user-icon-wrapper">{icon}</div>
		{children}
	</div>
);

const capitalizeFirstLetter = (str: string): string => {
	if (!str) return '';
	return str.charAt(0).toUpperCase() + str.slice(1);
};

interface BankAccountInformationProps {
	icon: React.ReactElement;
	accountHolder?: string;
	iban?: string;
}

const BankAccountInformation: FC<BankAccountInformationProps> = ({ accountHolder, iban, icon }) => {
	const { t } = useAppTranslation();

	return (
		<InfoSection icon={icon} className="translated-personal-info">
			<div className="bank-info-container">
				<div>{t(`results-page.header.account owner`)}:</div>
				<div style={{ marginTop: '2px' }}>IBAN:</div>
			</div>
			<div className="bank-info-content">
				<div>{accountHolder || t('results-page.header.not-available')}</div>
				<div>{iban || t('results-page.header.not-available')}</div>
			</div>
		</InfoSection>
	);
};
